import { createSlice } from "@reduxjs/toolkit";
import { getAuthTokenRequest, getProfileRequest, updateProfileRequest } from "../../api/userService";

const userSlice = createSlice({
	name: "User",
	initialState: {
		token: null,
		country: null,
		language: "en",
		profile: {},
		isAuthenticating: false,
		authError: null,
		authenticated: false,
		authPending: false,
	},
	reducers: {
		setToken(state, action) {
			state.token = action.payload;
		},
		setCountry(state, action) {
			state.country = action.payload;
		},
		setLanguage(state, action) {
			state.language = action.payload;
		},
		setUser(state, action) {
			state.profile = action.payload;
		},
		setAuthenticating(state, action) {
			state.isAuthenticating = action.payload;
		},
		setAuthError(state, action) {
			state.authError = action.payload;
		},
		setAuthenticated(state, action) {
			state.authenticated = action.payload;
		},
		setAuthPending(state, action) {
			state.authPending = action.payload;
		},
	},
});

export const { setToken, setCountry, setLanguage, setUser, setAuthenticating, setAuthError, setAuthenticated, setAuthPending } = userSlice.actions;

// New token-based authentication action
export const authenticateWithToken = (token, userCountry, userLanguage) => async (dispatch) => {
	try {
		// Set auth pending and clear previous errors
		dispatch(setAuthPending(true));
		dispatch(setAuthError(null));
		dispatch(setAuthenticated(false));

		// Store the token in Redux state
		dispatch(setToken(token));

		// Set country and language if provided
		if (userCountry) {
			dispatch(setCountry(userCountry));
		}
		if (userLanguage) {
			dispatch(setLanguage(userLanguage));
		}

		// The SDK initialization and user profile fetch will be handled by the useEffect hooks
		// in MainScreen.js when the token is available
	} catch (error) {
		console.error("Token authentication error:", error);
		dispatch(setAuthError(error.message));
		dispatch(setAuthenticated(false));
		dispatch(setAuthPending(false));
	}
};

// Your existing actions
export const getTokenRequest = (userId, userCountry, userLanguage) => async (dispatch) => {
	try {
		if (!!userCountry) {
			dispatch(setCountry(userCountry));
		}
		if (!!userLanguage) {
			dispatch(setLanguage(userLanguage));
		}

		// You can decide if you want to keep this or rely solely on Firebase auth
		// For now, I'm keeping it as a fallback
		const tokenResponse = await getAuthTokenRequest(userId);
		if (!tokenResponse) return;

		const tokenResponseJSON = await tokenResponse.json();
		if (!tokenResponseJSON) return;

		console.log(tokenResponseJSON);

		dispatch(setToken(tokenResponseJSON.id_token));
	} catch (error) {
		console.error("Token request error:", error);
		dispatch(setAuthError(error.message));
	}
};

export const getUserRequest = (fansUnitedSDK) => async (dispatch) => {
	try {
		const profileResponse = await getProfileRequest(fansUnitedSDK);
		if (!profileResponse) {
			dispatch(setAuthError("Failed to get user profile"));
			dispatch(setAuthenticated(false));
			dispatch(setAuthPending(false));
			return;
		}

		// Success: set user profile and mark as authenticated
		dispatch(setUser(profileResponse));
		dispatch(setAuthenticated(true));
		dispatch(setAuthPending(false));
		dispatch(setAuthError(null));
	} catch (error) {
		console.error("User request error:", error);
		dispatch(setAuthError(error.message || "Authentication failed"));
		dispatch(setAuthenticated(false));
		dispatch(setAuthPending(false));
	}
};

export default userSlice.reducer;
